import React, { useEffect, useState } from "react";
import ExtensionInputGroup, { StyledLabel, StyledToggleLabel } from "./ExtensionInputGroup";
import { useExtensionContext } from "../ExtensionContext";
import styled from "styled-components";
import { DEAL_WON, dealChannelTypesOptions, dealMarketingTypesOptions, TOGGLE_OPTIONS } from "../utils/constants";
import Toggle from "app/popleads/components/ui-kit/Toggle";
import ButtonWithText from "app/popleads/components/ui-kit/Button";
import moment from "moment";
import useActivityPreview from "app/popleads/hooks/useActivityPreview";

const ExtensionConsultingDeal = ({ dealObject, setDealObject, validateField }) => {
    const { companyUsers } = useExtensionContext();
    const StyledContainer = styled.div`
        display: flex;
        flex-direction: column;
        gap: 16px;
    `;

    const handleInputChange = (field, value) => {
        if (validateField(field, value)) {
            setDealObject({ ...dealObject, [field]: value });
        }
    };

    useEffect(() => {
        if (!dealObject.closingDate) {
            setDealObject({ ...dealObject, closingDate: moment().valueOf() });
        }

        if (!dealObject.partner) {
            const partnerUserId = companyUsers?.find((item) => item.name === dealObject.partnerName)?.id;
            if (partnerUserId) {
                setDealObject({ ...dealObject, partner: dealObject.partnerName, partnerUserId: partnerUserId });
            }
        }
    }, [dealObject, companyUsers]);

    const { getActivityPreview } = useActivityPreview();
    const [activityPreview, setActivityPreview] = useState(null);

    useEffect(() => {
        if (dealObject.activityId) {
            getActivityPreview(dealObject.activityId).then((data) => {
                setActivityPreview(data.activity);
            });
        }
    }, [dealObject.activityId]);

    let courseNameLabel = "";
    let newCourseNameLabel = "";
    if (dealObject.dealCampaignType === "E-Learning" || dealObject.dealCampaignType === "EdTech") {
        courseNameLabel = "E-Learning Bundles";
        newCourseNameLabel = "New E-Learning Bundles";
    } else if (dealObject.dealCampaignType === "Consulting") {
        courseNameLabel = "Type of Consulting";
        newCourseNameLabel = "New Consulting Type";
    } else if (dealObject.dealCampaignType === "Coaching") {
        courseNameLabel = "Type of Coaching";
        newCourseNameLabel = "New Coaching Type";
    }
    const courseNameOptions = [
        ...(activityPreview?.topicList || []).map((item) => ({
            label: item.topicName,
            value: item.topicName,
        })),
        { label: "Type a new " + courseNameLabel, value: "newCourseName" },
    ];

    return (
        <StyledContainer>
            {dealObject?.toBe === DEAL_WON && (
                <>
                    <ExtensionInputGroup
                        label="Deal Name"
                        type="input"
                        inpType="text"
                        onChange={(e) => {
                            const value = e.target ? e.target.value : e;
                            handleInputChange("ad", value);
                        }}
                        value={dealObject.ad}
                        placeholder="Enter Deal Name"
                    />

                    <ExtensionInputGroup
                        label={courseNameLabel}
                        type="select"
                        inpType="text"
                        onChange={(selected) => {
                            handleInputChange("courseName", selected.value);
                        }}
                        options={courseNameOptions}
                        value={
                            dealObject.courseName
                                ? dealObject.courseName === "newCourseName"
                                    ? { label: "Type a new " + courseNameLabel, value: "newCourseName" }
                                    : { label: dealObject.courseName, value: dealObject.courseName }
                                : ""
                        }
                        placeholder={`Select ${courseNameLabel}`}
                    />

                    {dealObject?.courseName === "newCourseName" && (
                        <ExtensionInputGroup
                            label={`New ${courseNameLabel}`}
                            type="input"
                            inpType="text"
                            onChange={(e) => {
                                const value = e.target ? e.target.value : e;
                                handleInputChange("newCourseName", value);
                            }}
                            value={dealObject.newCourseName}
                            placeholder={`Enter ${newCourseNameLabel}`}
                        />
                    )}

                    <ExtensionInputGroup
                        label="Account Name"
                        type="input"
                        inpType="text"
                        onChange={(e) => {
                            const value = e.target ? e.target.value : e;
                            handleInputChange("accountName", value);
                        }}
                        value={dealObject.accountName}
                        placeholder="Enter Account Name"
                    />

                    <ExtensionInputGroup
                        label="Unit Amount USD"
                        type="input"
                        inpType="number"
                        leftIcon="dollar-sign"
                        onChange={(e) => {
                            const value = e.target ? e.target.value : e;
                            handleInputChange("amount", value);
                        }}
                        value={dealObject.amount}
                        placeholder="Enter Unit Amount"
                    />

                    <div>
                        <StyledLabel>Number of Units</StyledLabel>
                        <div style={{ display: "flex", gap: "16px", marginTop: "4px" }}>
                            <ButtonWithText
                                icon="remove"
                                onClick={() => {
                                    const currentValue = Number(dealObject.numberOfUnits) || 1;
                                    if (currentValue > 1) {
                                        handleInputChange("numberOfUnits", currentValue - 1);
                                    }
                                }}
                                iconType="material"
                                variant="passive"
                                buttonStyle="default"
                                size="circle"
                            />
                            {dealObject.numberOfUnits}
                            <ButtonWithText
                                icon="add"
                                onClick={() => {
                                    const currentValue = Number(dealObject.numberOfUnits) || 0;
                                    handleInputChange("numberOfUnits", currentValue + 1);
                                }}
                                iconType="material"
                                variant="primary"
                                buttonStyle="default"
                                size="circle"
                            />
                        </div>
                    </div>
                </>
            )}

            {dealObject?.toBe !== DEAL_WON && (
                <ExtensionInputGroup
                    label="Unit Amount USD"
                    type="input"
                    inpType="number"
                    leftIcon="dollar-sign"
                    onChange={(e) => {
                        const value = e.target ? e.target.value : e;
                        handleInputChange("amount", value);
                    }}
                    value={dealObject.amount}
                    minLength={1}
                    placeholder="Enter Unit Amount"
                />
            )}
            <ExtensionInputGroup
                label="Partner"
                type="select"
                loadOptions={(input, callback) =>
                    callback(
                        companyUsers
                            .filter((user) => user.name.toLowerCase().includes(input.toLowerCase()))
                            .map((item) => {
                                return {
                                    label: item.name,
                                    value: item.id,
                                };
                            })
                            .slice(0, 10)
                    )
                }
                onChange={(selected) => {
                    if (selected) {
                        setDealObject({
                            ...dealObject,
                            partner: selected.value,
                            partnerName: selected.label,
                            partnerUserId: selected.value,
                        });
                    } else {
                        setDealObject({ ...dealObject, partnerName: "", partnerUserId: "", partner: "" });
                    }
                }}
                defaultOptions
                value={
                    dealObject.partner
                        ? {
                              label: dealObject.partnerName,
                              value: dealObject.partnerUserId,
                          }
                        : ""
                }
                placeholder="Select Partner"
                isClearable={true}
            />

            {dealObject.status === DEAL_WON && (
                <>
                    <ExtensionInputGroup
                        label="Set Closing Date"
                        type="date"
                        onChange={(date) => {
                            handleInputChange("closingDate", date);
                        }}
                        value={dealObject.closingDate}
                        placeholder="Select Closing Date"
                        dateFormat={"dd/MM/yyyy"}
                    />
                    <ExtensionInputGroup
                        label="Type"
                        type="select"
                        options={dealMarketingTypesOptions}
                        value={dealObject.type ? { label: dealObject.type, value: dealObject.type } : ""}
                        onChange={(selected) => {
                            handleInputChange("type", selected.value);
                        }}
                        placeholder="Select Type"
                    />

                    {(dealObject.type === "Marketing" || dealObject.type === "Marketing Cross Sale") && (
                        <ExtensionInputGroup
                            label="Select Marketing Channel"
                            type="select"
                            options={dealChannelTypesOptions}
                            value={
                                dealObject.channelType
                                    ? { label: dealObject.channelType, value: dealObject.channelType }
                                    : ""
                            }
                            onChange={(selected) => {
                                handleInputChange("channelType", selected.value);
                            }}
                            placeholder="Select Marketing Channel"
                        />
                    )}
                    <ExtensionInputGroup
                        label="Paid Amount"
                        type="input"
                        min={0}
                        minLength={1}
                        inpType="number"
                        leftIcon="dollar-sign"
                        onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "e" || e.key === "+") {
                                e.preventDefault();
                            }
                        }}
                        onChange={(e) => {
                            const value = e.target ? e.target.value : e;
                            handleInputChange("paidAmount", value);
                        }}
                        value={dealObject.paidAmount}
                        disabled={dealObject?.createAnotherDeal || dealObject?.toBe === DEAL_WON}
                        placeholder="Enter Paid Amount"
                    />
                    <div className="d-flex align-items-center justify-content-between">
                        <StyledToggleLabel>Vat Included</StyledToggleLabel>
                        <Toggle
                            setSelected={(value) => {
                                handleInputChange("vatIncludes", value);
                            }}
                            selected={dealObject.vatIncludes}
                            options={TOGGLE_OPTIONS}
                        />
                    </div>
                </>
            )}

            {(dealObject.status === "Lost" || dealObject.status === "Cancelled") && (
                <>
                    <ExtensionInputGroup
                        label="Set Closing Date"
                        type="date"
                        onChange={(date) => {
                            handleInputChange("closingDate", date);
                        }}
                        value={dealObject.closingDate}
                        placeholder="Select Closing Date"
                        dateFormat={"dd/MM/yyyy"}
                    />
                </>
            )}
        </StyledContainer>
    );
};

export default ExtensionConsultingDeal;
